(() => {
  const ROOT_SELECTOR =
    '[data-mr-cart-drawer-root]';

  const getFocusableElements = (container) => {
    if (!container) return [];

    return Array.from(
      container.querySelectorAll(
        [
          'a[href]',
          'button:not([disabled])',
          'select:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(',')
      )
    ).filter((element) => {
      return element.offsetParent !== null;
    });
  };

  const fetchCart = async () => {
    const root =
      window.Shopify?.routes?.root || '/';

    const response = await fetch(
      `${root}cart.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Unable to load cart.');
    }

    return response.json();
  };

  const refreshCartDrawer = async (root) => {
    const sectionId =
      root.dataset.sectionId;

    const shopRoot =
      window.Shopify?.routes?.root || '/';

    const response = await fetch(
      `${shopRoot}cart?section_id=${encodeURIComponent(sectionId)}`,
      {
        headers: {
          Accept: 'text/html'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to refresh cart drawer.'
      );
    }

    const html = await response.text();
    const parser = new DOMParser();
    const documentFragment =
      parser.parseFromString(
        html,
        'text/html'
      );

    const replacement =
      documentFragment.querySelector(
        ROOT_SELECTOR
      );

    if (!replacement) {
      throw new Error(
        'Cart drawer section missing.'
      );
    }

    root.innerHTML = replacement.innerHTML;
  };

  const updateHeaderCount = (cart) => {
    document
      .querySelectorAll('[data-mr-cart-count]')
      .forEach((counter) => {
        counter.textContent =
          String(cart.item_count);

        counter.classList.toggle(
          'is-empty',
          cart.item_count === 0
        );
      });

    document
      .querySelectorAll('[data-mr-cart-trigger]')
      .forEach((trigger) => {
        trigger.setAttribute(
          'aria-label',
          `Cart with ${cart.item_count} items`
        );
      });
  };

  const initializeCartDrawer = (root) => {
    if (
      !(root instanceof HTMLElement) ||
      root.dataset.mrCartInitialized === 'true'
    ) {
      return;
    }

    const drawer = root.querySelector(
      '[data-mr-cart-drawer]'
    );

    const overlay = root.querySelector(
      '[data-mr-cart-overlay]'
    );

    if (!drawer || !overlay) return;

    const drawerId =
      `cart-drawer-${drawer.id}`;

    let lastFocusedElement = null;

    const performOpen = () => {
      lastFocusedElement =
        document.activeElement
        instanceof HTMLElement
          ? document.activeElement
          : null;

      drawer.classList.add('is-open');
      drawer.setAttribute(
        'aria-hidden',
        'false'
      );

      overlay.classList.add('is-visible');
      overlay.removeAttribute('tabindex');

      window.requestAnimationFrame(() => {
        root
          .querySelector('[data-mr-cart-close]')
          ?.focus({ preventScroll: true });
      });
    };

    const performClose = ({
      restoreFocus = true
    } = {}) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute(
        'aria-hidden',
        'true'
      );

      overlay.classList.remove('is-visible');
      overlay.setAttribute(
        'tabindex',
        '-1'
      );

      if (
        restoreFocus &&
        lastFocusedElement
        instanceof HTMLElement
      ) {
        lastFocusedElement.focus({
          preventScroll: true
        });
      }
    };

    window.MRDrawerManager?.register(
      drawerId,
      {
        open: performOpen,
        close: performClose,
        isOpen: () =>
          drawer.classList.contains('is-open')
      }
    );

    root.dataset.mrDrawerId = drawerId;
    root.dataset.mrCartInitialized = 'true';

    drawer.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Tab') return;

        const focusable =
          getFocusableElements(drawer);

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last =
          focusable[focusable.length - 1];

        if (
          event.shiftKey &&
          document.activeElement === first
        ) {
          event.preventDefault();
          last.focus();
        }

        if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    );
  };

  const getRoot = () => {
    return document.querySelector(
      ROOT_SELECTOR
    );
  };

  const getDrawerId = () => {
    return getRoot()?.dataset.mrDrawerId;
  };

  const openCart = () => {
    const drawerId = getDrawerId();

    if (drawerId && window.MRDrawerManager) {
      window.MRDrawerManager.open(drawerId);
    }
  };

  const closeCart = () => {
    const drawerId = getDrawerId();

    if (drawerId && window.MRDrawerManager) {
      window.MRDrawerManager.close(drawerId);
    }
  };

  const setLoading = (loading) => {
    getRoot()?.classList.toggle(
      'is-loading',
      loading
    );
  };

  const refreshAndOpen = async () => {
    const root = getRoot();

    if (!root) return;

    setLoading(true);

    try {
      await refreshCartDrawer(root);

      root.dataset.mrCartInitialized = 'false';
      initializeCartDrawer(root);

      const cart = await fetchCart();
      updateHeaderCount(cart);

      openCart();
    } finally {
      setLoading(false);
    }
  };

  const changeLine = async (
    key,
    quantity
  ) => {
    const shopRoot =
      window.Shopify?.routes?.root || '/';

    setLoading(true);

    try {
      const response = await fetch(
        `${shopRoot}cart/change.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Unable to update cart.'
        );
      }

      await refreshAndOpen();
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const addVariant = async (
    variantId,
    button
  ) => {
    const shopRoot =
      window.Shopify?.routes?.root || '/';

    const originalText =
      button?.textContent || '';

    if (button) {
      button.disabled = true;
      button.textContent = 'ADDING…';
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${shopRoot}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1
              }
            ]
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Unable to add product.'
        );
      }

      await refreshAndOpen();
    } catch (error) {
      console.error(error);

      if (button) {
        button.disabled = false;
        button.textContent = originalText;
      }

      setLoading(false);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const cartTrigger =
        event.target.closest(
          '[data-mr-cart-trigger]'
        );

      if (cartTrigger) {
        event.preventDefault();
        openCart();
        return;
      }

      if (
        event.target.closest(
          '[data-mr-cart-close], [data-mr-cart-overlay], [data-mr-cart-keep-shopping]'
        )
      ) {
        event.preventDefault();
        closeCart();
        return;
      }

      const item = event.target.closest(
        '[data-mr-cart-item]'
      );

      if (item) {
        const key = item.dataset.cartKey;

        const currentQuantity =
          Number(
            item.querySelector(
              '[data-mr-cart-quantity-value]'
            )?.textContent
          ) || 0;

        if (
          event.target.closest(
            '[data-mr-cart-decrease]'
          )
        ) {
          changeLine(
            key,
            Math.max(0, currentQuantity - 1)
          );
          return;
        }

        if (
          event.target.closest(
            '[data-mr-cart-increase]'
          )
        ) {
          changeLine(
            key,
            currentQuantity + 1
          );
          return;
        }

        if (
          event.target.closest(
            '[data-mr-cart-remove]'
          )
        ) {
          changeLine(key, 0);
          return;
        }
      }

      const recommendationButton =
        event.target.closest(
          '[data-mr-cart-recommendation-add]'
        );

      if (recommendationButton) {
        const card =
          recommendationButton.closest(
            '[data-mr-cart-recommendation]'
          );

        const selector = card?.querySelector(
          '[data-mr-cart-recommendation-variant]'
        );

        const variantId =
          selector?.value ||
          recommendationButton.dataset.variantId;

        addVariant(
          variantId,
          recommendationButton
        );
      }
    }
  );

  document.addEventListener(
    'mr:cart-updated',
    () => {
      refreshAndOpen().catch(
        console.error
      );
    }
  );

  const initializeAll = () => {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initializeCartDrawer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAll
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    initializeAll
  );
})();
